import React, { useState, useEffect } from 'react';

const CustomCursor = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPointer, setIsPointer] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isFinePointer, setIsFinePointer] = useState(true);

  useEffect(() => {
    // Only enable custom cursor on devices with a fine pointer (mouse/trackpad)
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    setIsFinePointer(finePointer);
    if (!finePointer) return;

    const updatePosition = (e) => {
      setPosition({ x: e.clientX, y: e.clientY });
    };

    const updatePointer = (e) => {
      const target = e.target;
      const computedStyle = window.getComputedStyle(target);
      const isClickable = 
        computedStyle.cursor === 'pointer' ||
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.closest('button') ||
        target.closest('a') ||
        target.closest('[role="button"]') ||
        target.closest('[onClick]') ||
        target.closest('[class*="cursor-pointer"]');

      setIsPointer(isClickable);
      
      // Check if element is a link
      const isLinkElement = 
        target.tagName === 'A' || 
        target.closest('a') ||
        target.closest('[class*="hover:text-primary"]');

      setIsLink(isLinkElement);
    };

    const handleMouseEnter = () => setIsVisible(true);
    const handleMouseLeave = () => setIsVisible(false);

    // Add global style to force cursor: none on all elements
    const style = document.createElement('style');
    style.innerHTML = `
      * {
        cursor: none !important;
      }
      .react-player, 
      .react-player > div,
      .react-player iframe,
      .react-player iframe *,
      .react-player video,
      .react-player video * {
        cursor: none !important;
        pointer-events: auto !important;
      }
      @media (hover: none) {
        * {
          cursor: auto !important;
        }
      }
    `;
    document.head.appendChild(style);

    // Function to handle ReactPlayer elements
    const handleReactPlayer = () => {
      const players = document.querySelectorAll('.react-player');
      players.forEach(player => {
        // Force cursor style on the player container
        player.style.cursor = 'none';
        
        // Handle the iframe inside ReactPlayer
        const iframe = player.querySelector('iframe');
        if (iframe) {
          iframe.style.cursor = 'none';
          
          // Try to access iframe content
          try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            if (iframeDoc) {
              iframeDoc.body.style.cursor = 'none';
              // Add styles to iframe content
              const iframeStyle = iframeDoc.createElement('style');
              iframeStyle.textContent = `
                * {
                  cursor: none !important;
                }
              `;
              iframeDoc.head.appendChild(iframeStyle);
            }
          } catch (e) {
            // Cross-origin iframe access might fail, that's okay
            console.log('Could not access iframe content');
          }
        }
      });
    };

    // Initial setup
    handleReactPlayer();

    // Set up mutation observer for ReactPlayer elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          handleReactPlayer();
        }
      });
    });
    
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'src']
    });

    document.addEventListener('mousemove', updatePosition);
    document.addEventListener('mouseover', updatePointer);
    document.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', updatePosition);
      document.removeEventListener('mouseover', updatePointer);
      document.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.head.removeChild(style);
      observer.disconnect();
    };
  }, []);

  if (!isFinePointer || !isVisible) return null;

  return (
    <>
      {/* Main cursor */}
      <div
        className="fixed pointer-events-none z-50 mix-blend-difference"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div className={`w-4 h-4 rounded-full transition-all duration-200 ease-out
          ${isPointer ? 'scale-150' : 'scale-100'}
          ${isLink ? 'bg-primary-400' : 'bg-white'}`}
        />
      </div>
      
      {/* Cursor ring */}
      <div
        className="fixed pointer-events-none z-50"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div className={`w-8 h-8 rounded-full border-2 transition-all duration-300 ease-out
          ${isPointer ? 'scale-150 opacity-50' : 'scale-100 opacity-100'}
          ${isLink ? 'border-primary-400' : 'border-primary-400'}`}
        />
      </div>
    </>
  );
};

export default CustomCursor; 